import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DummyUserService } from "@/api";

/**
 * Default error handler to display error messages using react-toastify.
 * @param {object} err - The error object from the mutation.
 */
const defaultError = (err) => {
  const message = err?.response?.data?.message || err?.message || "An error occurred. Please try again.";
  toast.error(message);
};

/**
 * Custom hook for fetching dummy user list.
 *
 * @param {Object} params - Query parameters (page, limit, search, online_status)
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useListDummyUser = (params = {}, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["dummy-user-list", params],
    queryFn: () => {
      return DummyUserService.getList(params);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Custom hook for fetching a single dummy user.
 *
 * @param {string} uuid - Dummy User UUID
 * @param {function} onSuccess - Callback to execute when the query is successful.
 * @param {function} [onError=defaultError] - Callback to execute when the query fails.
 *
 * @returns {object} Query object from `useQuery`.
 */
const useGetDummyUser = (uuid, onSuccess, onError = defaultError) => {
  return useQuery({
    queryKey: ["dummy-user", uuid],
    queryFn: () => {
      return DummyUserService.get(uuid);
    },
    onSuccess: (data) => {
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      onError(error);
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    staleTime: 30000,
    cacheTime: 300000,
    refetchOnWindowFocus: false,
    enabled: !!uuid,
  });
};

/**
 * Custom hook for adding a new dummy user.
 *
 * @param {function} onSuccess - Callback to run if the add request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the add request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useAddDummyUser = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => {
      return DummyUserService.add(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["dummy-user-list"] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for updating an existing dummy user.
 *
 * @param {function} onSuccess - Callback to run if the update request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the update request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useUpdateDummyUser = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => {
      return DummyUserService.update(data);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["dummy-user-list"] });
      queryClient.invalidateQueries({ queryKey: ["dummy-user", variables?.uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for toggling a dummy user's online/offline status.
 *
 * @param {function} onSuccess - Callback to run if the request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useUpdateDummyUserStatus = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ uuid, isOnline }) => {
      return DummyUserService.updateStatus(uuid, isOnline);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["dummy-user-list"] });
      queryClient.invalidateQueries({ queryKey: ["dummy-user", variables?.uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

/**
 * Custom hook for deleting a dummy user.
 *
 * @param {function} onSuccess - Callback to run if the delete request succeeds.
 * @param {function} [onError=defaultError] - Callback to run if the delete request fails.
 *
 * @returns {object} Mutation object from `useMutation`.
 */
const useDummyUserDelete = (onSuccess, onError = defaultError) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) => {
      return DummyUserService.delete(uuid);
    },
    onSuccess: (data, uuid) => {
      queryClient.invalidateQueries({ queryKey: ["dummy-user-list"] });
      queryClient.invalidateQueries({ queryKey: ["dummy-user", uuid] });
      if (onSuccess) onSuccess(data);
    },
    onError,
  });
};

export {
  useListDummyUser,
  useGetDummyUser,
  useAddDummyUser,
  useUpdateDummyUser,
  useUpdateDummyUserStatus,
  useDummyUserDelete,
};
